import {
  FluidSize,
  FluidSkeletonConfig,
  FluidSkeletonType,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import { Utils } from '../../utils/utils';

export interface FluidLoadingSkeletonBonesConfig extends FluidSkeletonConfig {
  maxWidth?: number;
  dark?: boolean;
  disableGutter?: boolean;
}

export const SkeletonCircle = (props: FluidLoadingSkeletonBonesConfig) => {
  const circleClass = {
    'fluid-skeleton-circle': true,
    'no-gutter': props.disableGutter,
    [props.size]: true,
  };
  return <div class={circleClass} />;
};

export const SkeletonImage = (props: FluidLoadingSkeletonBonesConfig) => {
  const imageClass = {
    'fluid-skeleton-image': true,
    [props.size]: true,
  };
  return (
    <div class={imageClass}>
      <SkeletonRectangle disableGutter={true} {...props} />
    </div>
  );
};

export const SkeletonRectangle = (props: FluidLoadingSkeletonBonesConfig) => {
  const rectangleClass = {
    'fluid-skeleton-rectangle': true,
    [props.size]: true,
    darkened: props.dark,
    pulse: !props.animationType,
    'no-gutter': props.disableGutter,
    [props.animationType]: true,
  };
  const dynamicStyle = {
    'max-width': `${props.maxWidth}%`,
  };
  return <div class={rectangleClass} style={dynamicStyle} />;
};

export const SkeletonTable = (props: FluidLoadingSkeletonBonesConfig) => {
  return (
    <fluid-grid remove-spacing>
      {Utils.arrayOf(props.rows).map((_: void, idx: number) => {
        return Utils.arrayOf(props.columns).map(() => {
          const rowClass = {
            'fluid-skeleton-table-header': idx === 0,
            'fluid-skeleton-table-body': idx !== 0,
          };
          return (
            <fluid-grid-item class={rowClass} colSm={12 / props.columns}>
              <SkeletonRectangle
                {...props}
                dark={idx === 0}
                size={FluidSize.EXTRA_SMALL}
                animationType={props.animationType}
              />
            </fluid-grid-item>
          );
        });
      })}
    </fluid-grid>
  );
};

export const SkeletonText = (props: FluidLoadingSkeletonBonesConfig) => {
  const randomLength = () => Math.floor(Math.random() * (50 - 15) + 15);
  return Utils.arrayOf(props.rows).map(() => {
    return (
      <SkeletonRectangle
        {...props}
        size={FluidSize.EXTRA_SMALL}
        maxWidth={randomLength()}
        animationType={props.animationType}
      />
    );
  });
};

export const SkeletonButtonGroup = (props) => {
  return (
    <fluid-grid remove-spacing>
      {Utils.arrayOf(props.buttons).map(() => {
        return (
          <fluid-grid-item colSm={12 / props.buttons}>
            <SkeletonRectangle
              {...props}
              size={FluidSize.MEDIUM}
              animationType={props.animationType}
            />
          </fluid-grid-item>
        );
      })}
    </fluid-grid>
  );
};

export const SkeletonCard = (
  props: FluidLoadingSkeletonBonesConfig,
  children
) => {
  const cardClass = {
    'fluid-skeleton-card': true,
  };
  return (
    <fluid-grid remove-spacing class={cardClass}>
      <fluid-grid-item colSm={12} class={'fluid-skeleton-card-header'}>
        <fluid-grid>
          <fluid-grid-item colSm={4}>
            <SkeletonRectangle
              {...props}
              dark
              size={FluidSize.EXTRA_SMALL}
              animationType={props.animationType}
            />
          </fluid-grid-item>
        </fluid-grid>
      </fluid-grid-item>
      <fluid-grid-item colSm={12} class={'fluid-skeleton-card-body'}>
        {children}
      </fluid-grid-item>
    </fluid-grid>
  );
};

export const SkeletonForm = (props: FluidLoadingSkeletonBonesConfig) => {
  const formClass = {
    'fluid-skeleton-form': true,
  };

  const randomColumns = () => Math.floor(Math.random() * (3 - 1 + 1)) + 1;

  return (
    <fluid-grid class={formClass}>
      {Utils.arrayOf(props.rows).map(() => {
        const columns = randomColumns();
        return Utils.arrayOf(columns).map(() => {
          return (
            <fluid-grid-item colSm={12} colMd={12 / columns}>
              {[
                <SkeletonText
                  skeletonType={FluidSkeletonType.TEXT}
                  rows={1}
                  animationType={props.animationType}
                />,
                <SkeletonRectangle
                  {...props}
                  size={FluidSize.SMALL}
                  animationType={props.animationType}
                />,
              ]}
            </fluid-grid-item>
          );
        });
      })}
    </fluid-grid>
  );
};
